import dayjs from '@/components/Dayjs';
import { OrganizationStatus, TasksType } from '@prisma/client';
import { z } from 'zod';
import ApiError from './error';

/**
 * Validation split ownership:
 * - assignment/organization/product-key schemas live here
 * - classroom/task lifecycle schemas live in validationClassroomTaskSchemas.ts
 * Keep file boundaries explicit while preserving the existing public exports.
 */

export const groupSetIdSchema = z.object({
  groupsetId: z.string().cuid(), // req.query is string
});

export const groupIdSchema = z.object({
  groupId: z.string().cuid(), // req.query is string
});

const groupSetDisplayNameMin = 3,
  groupSetDisplayNameMax = 30;

const groupSetErrorMessages = {
  displayNameMinError: `Group Set name must be at least ${groupSetDisplayNameMin} characters long`,
  displayNameMaxError: `Group Set name must be at most ${groupSetDisplayNameMax} characters long`,
};

export const groupSetCreateSchema = z.object({
  displayName: z
    .string()
    .trim()
    .min(groupSetDisplayNameMin, {
      message: groupSetErrorMessages.displayNameMinError,
    })
    .max(groupSetDisplayNameMax, {
      message: groupSetErrorMessages.displayNameMaxError,
    }),
});

// --------------- Assignment ---------------

const assignmentIdValidationSchema = z.object({
  assignmentId: z.string().cuid(),
});

export const validateAssignmentId = (assignmentId: string): string => {
  const result = assignmentIdValidationSchema.safeParse({ assignmentId });

  if (!result.success) throw ApiError.fromZodError(result.error);

  return result.data.assignmentId;
};

const assignmentCreateDisplayNameMin = 3,
  assignmentCreateDisplayNameMax = 30;

const assignmentCreateErrorMessages = {
  displayNameMinError: `Assignment name must be at least ${assignmentCreateDisplayNameMin} characters long`,
  displayNameMaxError: `Assignment name must be at most ${assignmentCreateDisplayNameMax} characters long`,
};
const assignmentDueDateFormat = 'YYYY-MM-DD';
const isDueDateTodayOrLater = (rawValue: string) => {
  const picked = dayjs(rawValue, assignmentDueDateFormat)
    .utc()
    .format(assignmentDueDateFormat);
  const today = dayjs().utc().format(assignmentDueDateFormat);
  return picked >= today;
};
const parseDueDate = (rawValue: string) =>
  dayjs(rawValue, assignmentDueDateFormat).toDate();

export const assignmentCreateSchema = z.object({
  displayName: z
    .string()
    .trim()
    .min(assignmentCreateDisplayNameMin, {
      message: assignmentCreateErrorMessages.displayNameMinError,
    })
    .max(assignmentCreateDisplayNameMax, {
      message: assignmentCreateErrorMessages.displayNameMaxError,
    }),
  taskId: z.string().cuid({ message: 'Please select a notebook' }),
  groupSetId: z
    .string()
    .cuid({ message: 'Please select a group set' })
    .nullable()
    .optional(),
  classroomId: z.string().cuid({ message: 'Classroom is required' }),
  dueAt: z
    .string()
    .refine((rawValue) => isDueDateTodayOrLater(rawValue), {
      message: 'Due date can’t be in the past',
    })
    .transform((rawValue) => parseDueDate(rawValue)),
  aiAgentEnabled: z.boolean().optional(),
  studentReportLoginEnabled: z.boolean().optional(),
});

export const assignmentUpdateSchema = z.object({
  assignmentType: z.nativeEnum(TasksType, {
    required_error: 'Assignment type is required',
  }),
  assignmentSetId: z.string().cuid().optional(),
  assignmentSuperSetId: z.string().cuid().optional(),
  displayName: z
    .string()
    .trim()
    .min(assignmentCreateDisplayNameMin, {
      message: assignmentCreateErrorMessages.displayNameMinError,
    })
    .max(assignmentCreateDisplayNameMax, {
      message: assignmentCreateErrorMessages.displayNameMaxError,
    })
    .optional(),
  dueAt: z
    .string()
    .optional()
    .refine((rawValue) => !rawValue || isDueDateTodayOrLater(rawValue), {
      message: 'Due date can’t be in the past',
    })
    .transform((rawValue) => (rawValue ? parseDueDate(rawValue) : undefined)),
  textLabelSetId: z.string().cuid().optional(),
  aiAgentEnabled: z.boolean().optional(),
  studentReportLoginEnabled: z.boolean().optional(),
});

export const assignmentQueryParamsSchema = z.object({
  classroomId: z.string().cuid().optional(),
  assignmentType: z.nativeEnum(TasksType).optional(),
  unit: z.string().optional(),
  limit: z.string().regex(/^\d+$/).transform(Number).optional(),
  // Optional additions to support pagination modes
  cursor: z.string().optional(),
  status: z.enum(['active', 'complete']).optional(),
  useSeparatePagination: z
    .enum(['true', 'false'])
    .transform((val) => val === 'true')
    .optional(),
  activeLimit: z.string().regex(/^\d+$/).transform(Number).optional(),
  expiredLimit: z.string().regex(/^\d+$/).transform(Number).optional(),
  activeCursor: z.string().optional(),
  expiredCursor: z.string().optional(),
});

export const classroomQueryParamsSchema = z.object({
  cursor: z.string().cuid().optional(),
  limit: z.string().regex(/^\d+$/).transform(Number).optional(),
  includeExpired: z
    .enum(['true', 'false'])
    .transform((val) => val === 'true')
    .optional(),
});

export const assignmentIdQueryParamsSchema = z.object({
  assignmentId: z.string().cuid(),
});

export const assignmentDeleteQueryParamsSchema = z.object({
  assignmentSetId: z.string().cuid().optional(),
  assignmentSuperSetId: z.string().cuid().optional(),
  assignmentType: z.nativeEnum(TasksType),
});

export const assignmentIdWithClassroomIdQueryParamsSchema = z.object({
  assignmentId: z.string().cuid(),
  assignmentSetId: z.string().cuid(),
  cursor: z.string().optional(),
  filters: z.string().optional(),
  includeAllTimeMetrics: z
    .enum(['true', 'false'])
    .transform((val) => val === 'true')
    .optional(),
});
export const assignmentFiltersParamsSchema = z.object({
  assignmentId: z.string().cuid(),
  assignmentSetId: z.string().cuid(),
});

export const challengeSuperSetReportSchema = z.object({
  assignmentSuperSetId: z.string().cuid(),
});

// --------------- Like Creation ---------------

const likeEntryTargetTypeSchema = z.enum([
  'engineeringNotebookStep',
  'kudo',
  'feelingsTracker',
  'gridStep',
  'image',
]);

export const likeCreateSchema = z.union([
  z.object({
    entryId: z.string().cuid(),
    entryType: likeEntryTargetTypeSchema,
    messageId: z.never().optional(),
  }),
  z
    .object({
      entryType: z.literal('message'),
      entryId: z.string().cuid().optional(),
      messageId: z.string().cuid().optional(),
    })
    .superRefine((data, ctx) => {
      if (!data.entryId && !data.messageId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Message likes require a messageId.',
          path: ['messageId'],
        });
      }

      if (data.entryId && data.messageId && data.entryId !== data.messageId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Message like entryId must match messageId.',
          path: ['entryId'],
        });
      }
    }),
]);

export const likeDeleteSchema = z.object({
  likeId: z.string().cuid(),
});

// --------------- Messages ---------------

export const messageCreateSchema = z.object({
  content: z.string().min(1).max(1000),
  entryId: z.string().cuid(),
  entryType: z.enum([
    'engineeringNotebookStep',
    'kudo',
    'feelingsTracker',
    'gridStep',
    'image',
  ]),
});

export const messageFormSchema = z.object({
  content: z
    .string()
    .min(1, {
      message: 'Message must be at least 1 character long',
    })
    .max(1000, {
      message: 'Message must be at most 1000 characters long',
    }),
});

export const messageQueryParamsSchema = z.object({
  assignmentId: z.string().cuid(),
});

export const messageUpdateReadStateSchema = z.object({
  assignmentId: z.string().cuid(),
  messageId: z.string().cuid(),
});

export const userSubscriptionEnrollSchema = z.object({
  productKey: z.string().trim().length(20, {
    message: 'Product key must be 20 characters long',
  }),
});

// --------------- Organizations -------------
export const OrganizationUpdateSchema = z
  .object({
    displayName: z.string().min(3).max(100).optional(),
    adminEmail: z.string().email().optional(), // Keep optional for admin changes
    location: z.string().max(100).nullable().optional(), // Allow setting to null
    notes: z.string().max(500).nullable().optional(),
    educatorLimit: z.coerce
      .number()
      .int()
      .min(1, { message: 'Educator limit must be at least 1.' })
      .optional(),
    studentLimit: z.coerce
      .number()
      .int()
      .min(1, { message: 'Student limit must be at least 1.' })
      .optional(),
    status: z.nativeEnum(OrganizationStatus).optional(), // Allow updating status
    customNotebookEnabled: z.boolean().optional(),
    notebookAiEnabled: z.boolean().optional(),
    aiAgentEnabled: z.boolean().optional(),
    studentReportLoginEnabled: z.boolean().optional(),
  })
  .strict();
export type OrganizationUpdateFormData = z.infer<
  typeof OrganizationUpdateSchema
>;

// Schema for creating a new organization (API request body)
export const OrganizationCreateSchema = z.object({
  displayName: z
    .string()
    .min(3, {
      message: 'Organization name must be at least 3 characters long.',
    })
    .max(100, { message: 'Organization name must be 100 characters or less.' }),
  // We expect the email of the user who will be the admin
  adminEmail: z
    .string()
    .email({ message: 'Invalid email address provided for the admin.' }),
  location: z
    .string()
    .max(100, { message: 'Location must be 100 characters or less.' })
    .nullable()
    .optional(),
  notes: z
    .string()
    .max(500, { message: 'Tag / Notes must be 500 characters or less.' })
    .nullable()
    .optional(),
  educatorLimit: z.coerce
    .number()
    .int()
    .min(1, { message: 'Educator limit must be at least 1.' })
    .max(500, {
      message: 'Educator limit must be at most 500.',
    })
    .optional()
    .default(50),
  studentLimit: z.coerce
    .number()
    .int()
    .min(1, { message: 'Student limit must be at least 1.' })
    .max(1000, {
      message: 'Student limit must be at most 1000.',
    })
    .optional()
    .default(200),
  customNotebookEnabled: z.boolean().optional().default(false),
  distributorKey: z
    .string()
    .trim()
    .max(64, { message: 'Organization access selection is invalid.' })
    .optional()
    .default(''),
});

export type OrganizationCreateFormData = z.infer<
  typeof OrganizationCreateSchema
>;

// You might also want a schema for GET requests if not already defined
export const OrganizationsGetSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(8), // Match frontend default
    search: z.string().optional(),
    sortBy: z
      .enum(['createdAt', 'displayName'])
      .optional()
      .default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
    availability: z
      .enum(['ALL', 'EXPLORE_ONLY', 'DISTRIBUTOR'])
      .optional()
      .default('EXPLORE_ONLY'),
    distributorKey: z.string().trim().min(1).max(64).optional(),
  })
  .superRefine(({ availability, distributorKey }, context) => {
    if (availability === 'DISTRIBUTOR' && !distributorKey) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['distributorKey'],
        message: 'A distributor is required for this availability filter.',
      });
    }
  });
export type OrganizationsGetQuery = z.infer<typeof OrganizationsGetSchema>;

export * from './validation/productKeySchemas';
